/**
 * Frontend API client
 * Handles all API calls to backend serverless functions
 * Context: Secure API abstraction layer for LostMind AI Video Lab
 */

import { ApiResponse, VideoMetadata, Project, User } from './types';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://video.lostmindai.com' 
  : 'http://localhost:3000';

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // User and authentication
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>('/user');
  }

  async updateUserCredits(amount: number): Promise<ApiResponse<User>> {
    return this.request<User>('/user/credits', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  // Video metadata
  async getVideoMetadata(url: string): Promise<ApiResponse<VideoMetadata>> {
    return this.request<VideoMetadata>('/video/metadata', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  // Video analysis
  async analyzeVideo(
    videoUrl: string,
    mode: string,
    options: Record<string, unknown> = {}
  ): Promise<ApiResponse<unknown>> {
    return this.request('/video/analyze', {
      method: 'POST',
      body: JSON.stringify({ videoUrl, mode, options }),
    });
  }

  // Learning app generation
  async generateLearningApp(
    videoUrl: string,
    customization: Record<string, unknown> = {}
  ): Promise<ApiResponse<{ spec: unknown; code: string }>> {
    return this.request('/video/generate-app', {
      method: 'POST',
      body: JSON.stringify({ videoUrl, customization }),
    });
  }

  // Projects
  async getProjects(): Promise<ApiResponse<Project[]>> {
    return this.request<Project[]>('/projects');
  }

  async getProject(id: string): Promise<ApiResponse<Project>> {
    return this.request<Project>(`/projects/${id}`);
  }

  async createProject(
    name: string,
    videoUrl: string
  ): Promise<ApiResponse<Project>> {
    return this.request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify({ name, videoUrl }),
    });
  }

  async updateProject(
    id: string,
    updates: Partial<Project>
  ): Promise<ApiResponse<Project>> {
    return this.request<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Stripe payments
  async createPaymentSession(
    priceId: string,
    quantity = 1
  ): Promise<ApiResponse<{ sessionId: string }>> {
    return this.request('/stripe/create-session', {
      method: 'POST',
      body: JSON.stringify({ priceId, quantity }),
    });
  }

  async getSubscriptionStatus(): Promise<ApiResponse<{
    isActive: boolean;
    tier: string;
    renewsAt?: string;
  }>> {
    return this.request('/stripe/subscription');
  }
}

// Export singleton instance
export const api = new ApiClient();